import { useState } from "react";
import { api } from "@/lib/api";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PACKAGES = [
  { id: "starter", amount: 10, label: "Supplies for a student" },
  { id: "supporter", amount: 20, label: "A day of programming" },
  { id: "advocate", amount: 50, label: "A week of programming" },
  { id: "champion", amount: 100, label: "Transportation & meals" },
];

export default function DonateWidget({ compact = false }) {
  const [selected, setSelected] = useState("supporter");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [loading, setLoading] = useState(false);

  const handleDonate = async () => {
    setLoading(true);
    try {
      const body = {
        origin_url: window.location.origin,
        donor_name: name,
        donor_email: email,
        frequency,
      };
      if (selected === "custom") {
        const amt = parseFloat(custom);
        if (!amt || amt < 1) {
          toast.error("Please enter a valid donation amount.");
          setLoading(false);
          return;
        }
        body.custom_amount = amt;
      } else {
        body.package_id = selected;
      }
      const res = await api.post("/donations/checkout", body);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="donate-widget"
      className="rounded-3xl bg-white border border-slate-200 shadow-xl p-6 sm:p-8"
    >
      <h3 className="font-display text-2xl font-extrabold text-brandnavy">Make a Donation</h3>
      <p className="mt-1 text-sm text-slate-500">Your gift directly funds programs for underserved youth.</p>

      <div className="mt-5 grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1" data-testid="donation-frequency-toggle">
        <button
          type="button"
          data-testid="frequency-one-time"
          onClick={() => setFrequency("one_time")}
          className={`rounded-full py-2.5 text-sm font-semibold transition-all ${
            frequency === "one_time" ? "bg-white text-brandnavy shadow-sm" : "text-slate-500 hover:text-brandnavy"
          }`}
        >
          One-time
        </button>
        <button
          type="button"
          data-testid="frequency-monthly"
          onClick={() => setFrequency("monthly")}
          className={`rounded-full py-2.5 text-sm font-semibold transition-all ${
            frequency === "monthly" ? "bg-white text-accent shadow-sm" : "text-slate-500 hover:text-brandnavy"
          }`}
        >
          Monthly
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {PACKAGES.map((p) => (
          <button
            key={p.id}
            data-testid={`donate-amount-${p.id}`}
            onClick={() => setSelected(p.id)}
            className={`rounded-2xl border p-4 text-left transition-all duration-200 ${
              selected === p.id
                ? "border-accent bg-orange-50 ring-2 ring-accent/40"
                : "border-slate-200 hover:border-slate-300 hover:-translate-y-0.5"
            }`}
          >
            <span className="block font-display text-2xl font-bold text-brandnavy">
              ${p.amount}
              {frequency === "monthly" && <span className="text-sm font-semibold text-slate-400">/mo</span>}
            </span>
            {!compact && <span className="block mt-1 text-xs text-slate-500 leading-snug">{p.label}</span>}
          </button>
        ))}
      </div>

      <button
        data-testid="donate-amount-custom"
        onClick={() => setSelected("custom")}
        className={`mt-3 w-full rounded-2xl border p-4 flex items-center gap-3 transition-all ${
          selected === "custom" ? "border-accent bg-orange-50 ring-2 ring-accent/40" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <span className="text-sm font-medium text-slate-700">Custom</span>
        <div className="flex items-center flex-1">
          <span className="text-slate-400 mr-1">$</span>
          <input
            data-testid="donate-custom-input"
            type="number"
            min="1"
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              setSelected("custom");
            }}
            placeholder="Enter amount"
            className="w-full bg-transparent outline-none text-brandnavy placeholder:text-slate-400"
          />
        </div>
      </button>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input
          data-testid="donor-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent/40"
        />
        <input
          data-testid="donor-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional)"
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent/40"
        />
      </div>

      <button
        data-testid="donate-submit-button"
        onClick={handleDonate}
        disabled={loading}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-semibold text-white shadow-md transition-all duration-300 hover:bg-orange-400 hover:-translate-y-0.5 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Heart className="h-5 w-5 fill-current" />}
        {loading ? "Redirecting…" : frequency === "monthly" ? "Give Monthly" : "Donate Securely"}
      </button>
      <p className="mt-3 text-center text-xs text-slate-400">
        {frequency === "monthly" ? "Recurring monthly gift · cancel anytime · " : ""}Secure payment powered by Stripe.
      </p>
    </div>
  );
}
